import { useState } from 'react';
import type { FieldControl } from '@platform/core/types';
import { controlOf } from '@platform/core/lib/fieldTypes';
import type { IInlineFieldProps } from './IInlineFieldProps';
import { isFieldRestricted } from './fieldRestricted';
import { inlineChanged, inlineValue } from './inlineValue';
import { resolveControl } from './registry';
import { RestrictedValue } from './RestrictedValue';
import './fields.css';

/** Контроли, що зберігаються одразу при виборі (пікери, дерево, особа, посилання, так/ні, теги, файл). */
const IMMEDIATE = new Set<FieldControl>(['picker', 'tree', 'user', 'lookup', 'switch', 'tags', 'file']);
/** Контроли-списки: порожня клітинка показує `emptyChoice` (текстові поля прототипу порожні без підказки). */
const CHOICES = new Set<FieldControl>(['picker', 'tree', 'user', 'lookup']);

/**
 * Клітинка редагування в рядку (UIUX/tables/inline-edit; прототип inlineRows): той самий контрол, що й у формі, розміру inline (30px,
 * прозорий до наведення). Вибір у пікерах, дереві, особі й посиланні зберігається одразу; текст, число й дата — при Enter або виході
 * з клітинки (Esc скасовує); довгий текст — у плаваючому редакторі, зберігається при його закритті. Поле з персональними даними, приховане
 * сервером, — «Приховано» без контрола (редагування в рядку, рядки підтаблиць).
 */
export function InlineField({ field, rt, ws, record, value, onCommit, readOnly, emptyChoice }: IInlineFieldProps) {
  const control = controlOf(field);
  const immediate = IMMEDIATE.has(control);
  const [draft, setDraft] = useState<{ v: unknown } | null>(null);
  const Control = resolveControl(control);
  if (isFieldRestricted(record, field)) return <span className="fc-cell"><RestrictedValue compact /></span>;
  const commit = () => {
    if (draft && inlineChanged(draft.v, value)) onCommit(inlineValue(draft.v));
    setDraft(null);
  };
  const change = (v: unknown) => {
    if (immediate) { if (inlineChanged(v, value)) onCommit(inlineValue(v)); return; }
    setDraft({ v });
  };
  return (
    <span className="fc-cell"
      onBlur={(e) => { if (!immediate && !e.currentTarget.contains(e.relatedTarget as Node | null)) commit(); }}
      onKeyDown={(e) => {
        if (immediate) return;
        if (e.key === 'Enter' && control !== 'textarea' && control !== 'json') { e.preventDefault(); commit(); }
        else if (e.key === 'Escape') setDraft(null);
      }}>
      <Control id={`c-${record.id}-${field.key}`} value={(draft ? draft.v : value) as never} onChange={change} field={field} rt={rt} ws={ws}
        size="inline" record={record} readOnly={readOnly}
        placeholder={CHOICES.has(control) ? emptyChoice : undefined} />
    </span>
  );
}
